import {
  Component, OnInit, Input, Output, EventEmitter, OnDestroy, ViewChild
} from '@angular/core';
import { accountTypeLabel, isCreditCardAccountType } from 'src/app/modules/setting/models/lists-models';
import { DailySyncMode, DailySyncState, TransactionDailySyncService } from 'src/app/data/service/transaction-daily-sync.service';
import { DailySyncDialogService } from './daily-sync/daily-sync-dialog.service';
import { TransactionsService, XeroRefreshStatusChange } from 'src/app/data/service/transactions.service';
import { timeout } from 'rxjs/operators';

/** How long Refresh waits for Xero before saying so, rather than spinning on. */
const REFRESH_TIMEOUT_MS = 3 * 60 * 1000;
import { NgxFileDropEntry, FileSystemFileEntry } from 'ngx-file-drop';
import { SimpleChanges, OnChanges } from '@angular/core';
import { FilterService } from 'src/app/data/service/filter.service';
import { OrganizationService } from 'src/app/data/service/organization.service';
import { ToasterMessageService } from 'src/app/shared/toaster-message/toaster-message.service';
import { ToasterTypes } from 'src/app/core/enum/toaster-type.enum';
import { UnreconciledReportService } from 'src/app/data/service/UnreconciledReport.service';
import { NewReport } from '../models/supplier/new-report';
import { ReportShort } from '../models/supplier/report-short';
import { PaymentAccount } from '../models/account/payment-account';
import { Unreconciledreportperiod } from 'src/app/core/enum/Unreconciledreport-period.enum';
import { ReportPeriod } from 'src/app/core/enum/report-period.enum';
import { FinancialYearEnd, resolveReportPeriod } from 'src/app/core/util/report-period';
import { AccountingIntegration } from 'src/app/data/enums/accounting-integration';
import { BookkeepingFilter } from '../models/filter/bookkeeping-filter';
import { BankFeedsService } from 'src/app/data/service/bank-feeds.service';
import {
  BankFeedAccount, BankFeedAvailability, BankFeedImportResult, BankFeedPreviewTransaction
} from 'src/app/data/model/bank-feed';
import { EntitlementsService } from 'src/app/core/service/entitlements.service';
import { MODULES } from 'src/app/core/config/module-codes';
import { SPREADSHEET_LOGO, XERO_LOGO } from './import-method-logos';
import moment from 'moment';
import * as $ from 'jquery';

export type ImportStep = 'method' | 'history' | 'bank' | 'xero' | 'spreadsheet' | 'map-columns' | 'map-accounts' | 'review' | 'refresh';

/** A status change in a Refresh's review, with whether it is ticked. */
export interface RefreshChangeRow extends XeroRefreshStatusChange {
  selected: boolean;
}

export interface ColumnMapping {
  required: string;        // display name e.g. 'Bank Account'
  requiredKey: string;     // lowercase key e.g. 'bank account'
  mapped: string | null;   // header from the file the user chose
}

export interface AccountMapping {
  csvName: string;            // account name from CSV
  mappedAccountName: string;  // org account name the user picked, '' if unmapped
}

export interface ValidationError {
  field: string;
  message: string;
  value?: string;
}

export interface ImportTransaction {
  description: string;
  amount: number;
  transactionDate: string;
  accountName: string;
  importStatus: string;
  isImport: boolean;
  rowNumber?: number;
  validationErrors?: ValidationError[];
  suggestedMatch?: SuggestedMatch;
  matchAccepted?: boolean;
  /** Xero import: already reconciled in Xero. Sent back on import so the row is stored as such. */
  isReconciled?: boolean;
  // User has explicitly chosen to import this row even though the backend
  // pre-flight flagged it as a duplicate. Sent through to the import SP,
  // which will skip its dedupe check for rows where this is true.
  allowDuplicate?: boolean;
  // SD-1207: the Stripe transaction a bank-feed row came from; what the import is told to bring in.
  bankTransactionId?: string;
}

export interface SuggestedMatch {
  invoiceId: string;
  supplierName: string;
  amount: number;
  date: string;
  matchConfidence: number;
}

@Component({
  selector: 'app-import-transactions',
  templateUrl: './import-transactions.component.html',
  styleUrls: ['./import-transactions.component.scss']
})
export class ImportTransactionsComponent implements OnInit, OnDestroy, OnChanges {
  ngOnChanges(changes: SimpleChanges): void {}

  @Input() reports: ReportShort[] = [];
  @Input() filter: BookkeepingFilter = new BookkeepingFilter();
  /** Open straight onto a step -- 'bank' after connecting a bank from the Transactions page. */
  @Input() initialStep: ImportStep | null = null;
  /**
   * The Transactions screen's import: a report is offered but not required, and none is
   * chosen to begin with. Expense Reports leaves this off, where an import is always into one.
   */
  @Input() reportOptional = false;
  @Output() importComplete = new EventEmitter<string>();
  @Output() closed = new EventEmitter<void>();

  step: ImportStep = 'method';

  // Report selection — bound to a ReportShort object (id='' means new/unsaved)
  selectedReport: ReportShort | null = null;

  /**
   * Reports available for selection — excludes Completed ones.
   * Prefers `incompleteReports` (loaded from the dedicated endpoint on init) and
   * falls back to the parent-supplied `reports` so the dropdown is still useful
   * if the API is slow / fails.
   */
  get selectableReports(): ReportShort[] {
    if (this.incompleteReports.length > 0) return this.incompleteReports;
    return this.reports.filter(r => r.status?.toLowerCase() !== 'completed');
  }

  newReport: NewReport;

  // ── SD-1207 Bank feed (Stripe Financial Connections) ───────
  /**
   * Whether the organisation holds the bank-feeds module. Governs whether the method
   * tile appears at all; `bankAvailability` then governs whether it is enabled. The two
   * are separate because "you don't have this feature" and "this feature is US-only"
   * are different conversations.
   */
  hasBankFeeds: boolean = false;
  bankAvailability: BankFeedAvailability = null;
  /** True while the availability call is in flight, so the tile never sits there blank. */
  bankAvailabilityChecking = false;
  bankFeeds: BankFeedAccount[] = [];
  bankLoading: boolean = false;
  bankBusy: boolean = false;
  bankError: string = '';
  /** Per-feed ledger account selection, keyed by feed id, before it is saved. */
  bankAccountChoice: { [connectionId: string]: string } = {};
  /** yyyy-MM-dd, bound to a native date input. Blank means as far back as Stripe goes. */
  bankImportFrom: string = '';
  bankImportResult: BankFeedImportResult = null;

  // Xero
  dateRangeSelected: string = '';
  bankAccountList: PaymentAccount[] = [];
  selectedBankAccounts: PaymentAccount[] = [];
  showDateRange: string = '';
  showCustomFilter: boolean = false;
  /** True only when the organisation's accounting integration is Xero. */
  isXeroAccount: boolean = false;
  readonly xeroLogo = XERO_LOGO;
  readonly spreadsheetLogo = SPREADSHEET_LOGO;
  /**
   * No Xero or QuickBooks. Only these organisations may create an account for a bank feed:
   * anyone else's chart of accounts belongs to their accounting system, and the server
   * refuses the create for them.
   */
  isNoAccounting: boolean = false;
  isXeroLoading: boolean = false;
  dropdownBankSettings = {};
  // Custom bank account dropdown
  bankDropdownOpen: boolean = false;
  bankSearch: string = '';

  // Custom range inputs (yyyy-MM-dd, bound to native date inputs)
  customStartDate: string = '';
  customEndDate: string = '';
  customRangeError: string = '';

  date = new Date();
  startDate: Date = new Date();
  lastDayOfMonth: Date = new Date();
  unreconciledreportperiod = Unreconciledreportperiod;
  reportPeriod = ReportPeriod;

  /**
   * Financial-year/lock-date settings for the current organisation.
   * Loaded from /api/Organisation/GetOrganisationFinancialSettings on init so the
   * "This Financial Year" / "Last Financial Year" pills honour the org's actual
   * fiscal calendar (FinancialYearEndMonth / FinancialYearEndDay).
   */
  financialYearEndMonth: number | null = null;   // 1-12
  financialYearEndDay: number | null = null;     // 1-31
  periodLockDate: Date | null = null;
  endOfYearLockDate: Date | null = null;

  // Incomplete reports loaded from the API (UnreconciledReportMaster where !Complete)
  incompleteReports: ReportShort[] = [];

  /**
   * ng-select tag factory — produces a synthetic ReportShort with id='' so we
   * know on confirm() to create rather than append.
   */
  addReportTag = (term: string): ReportShort => ({ id: '', reportName: term.trim() });

  // Spreadsheet
  fileObj: any = null;
  isFileUploading: boolean = false;
  uploadError: string | null = null;

  // Column mapping
  csvHeaders: string[] = [];
  csvText: string = '';
  columnMappings: ColumnMapping[] = [];

  // Account mapping
  accountMappings: AccountMapping[] = [];

  // Review. 'BankFeed' is the Stripe feed: its rows come from a preview and are imported
  // through bankfeeds/accounts/import rather than the spreadsheet or Xero endpoints.
  source: 'Spreadsheet' | 'Report' | 'BankFeed' | 'Refresh' = 'Spreadsheet';

  // ── Refresh: Xero's new transactions and status changes, reviewed before anything is written ──

  /** The status changes a Refresh found; the new transactions are the review's usual rows. */
  refreshChanges: RefreshChangeRow[] = [];
  /** How many accounts the Refresh compared with Xero, for the empty and loading states. */
  refreshAccountCount = 0;
  refreshError = '';
  /** The feed being reviewed, and changes to rows it already imported (applied on import). */
  reviewFeed: BankFeedAccount | null = null;
  bankPreviewChangeCount = 0;
  /** The feed whose preview is loading, so its card can say so. */
  bankReviewingId = '';
  validTransactions: ImportTransaction[] = [];
  invalidTransactions: ImportTransaction[] = [];
  searchQuery: string = '';
  isSaving: boolean = false;

  allTransactions: ImportTransaction[] = [];

  private rebuildAllTransactions(): void {
    this.allTransactions = [...this.validTransactions, ...this.invalidTransactions];
    this.rebuildGroupedTransactions();
  }

  /** The review list narrowed to one reconciliation state by the summary tiles; 'all' shows every row. */
  statusFilter: 'all' | 'toReconcile' | 'reconciled' = 'all';

  get filteredAll(): ImportTransaction[] {
    let rows = this.allTransactions;
    if (this.statusFilter === 'reconciled') rows = rows.filter(t => !!t.isReconciled);
    if (this.statusFilter === 'toReconcile') rows = rows.filter(t => !t.isReconciled);
    if (!this.searchQuery) return rows;
    const q = this.searchQuery.toLowerCase();
    return rows.filter(t =>
      t.description?.toLowerCase().includes(q) ||
      t.accountName?.toLowerCase().includes(q) ||
      String(t.amount).includes(q) ||
      t.transactionDate?.includes(q)
    );
  }

  setStatusFilter(filter: 'all' | 'toReconcile' | 'reconciled'): void {
    this.statusFilter = this.statusFilter === filter ? 'all' : filter;
    this.rebuildGroupedTransactions();
  }

  // What the import holds, by reconciliation state: Xero sends reconciled rows too, and they
  // import as reconciled, so the review says plainly which are which.
  get reconciledCount(): number { return this.allTransactions.filter(t => !!t.isReconciled).length; }
  get toReconcileCount(): number { return this.allTransactions.filter(t => !t.isReconciled).length; }
  get selectedReconciledCount(): number {
    return this.allTransactions.filter(t => t.isImport && this.isReimportable(t) && !!t.isReconciled).length;
  }
  get selectedToReconcileCount(): number {
    return this.allTransactions.filter(t => t.isImport && this.isReimportable(t) && !t.isReconciled).length;
  }

  /** One plain line under the expense report box: what importing will do with the choice made. */
  get reportHint(): string {
    if (this.selectedReport && this.isExistingReport) return 'The transactions are added to this report.';
    if (this.selectedReport) return 'A new report is created with this name, holding the transactions.';
    return this.reportOptional
      ? 'Optional. Leave it empty to import the transactions without a report.'
      : 'Pick a report, or type a name to create one.';
  }

  /**
   * True when the row is a duplicate that the backend already knows about
   * (the dedupe SP will silently drop it on re-submit). The Xero pre-check
   * sets `importStatus` to a value containing "exists", e.g. "Already Exists".
   * Detection is also done on a per-error basis so future error shapes stay supported.
   */
  isAlreadyExists(tx: ImportTransaction): boolean {
    const status = (tx.importStatus || '').toLowerCase();
    if (status.includes('exist')) return true;
    return (tx.validationErrors || []).some(
      e => (e.message || '').toLowerCase().includes('exist')
    );
  }

  /** User has explicitly chosen to import this duplicate anyway. */
  isOverridden(tx: ImportTransaction): boolean {
    return !!tx.allowDuplicate;
  }

  /**
   * True when the row can actually be imported — i.e. it's either not a
   * duplicate, or it is a duplicate that the user has explicitly overridden.
   * Drives checkbox enabled-ness, group toggles, and the final submit filter.
   */
  isReimportable(tx: ImportTransaction): boolean {
    return !this.isAlreadyExists(tx) || this.isOverridden(tx);
  }

  /**
   * Flip the override flag on a duplicate row. Overriding pre-checks the row
   * (the user just expressed intent to import); un-overriding clears it again
   * so we can't accidentally submit a row the user thought was locked out.
   */
  toggleOverride(tx: ImportTransaction): void {
    if (!this.isAlreadyExists(tx)) return;
    tx.allowDuplicate = !tx.allowDuplicate;
    tx.isImport = !!tx.allowDuplicate;
  }

  /** Count of rows the backend pre-flight flagged as duplicates. */
  get alreadyExistsCount(): number {
    return this.allTransactions.filter(t => this.isAlreadyExists(t)).length;
  }

  /** Count of duplicate rows the user has overridden — included in the banner copy. */
  get overriddenCount(): number {
    return this.allTransactions.filter(t => this.isAlreadyExists(t) && this.isOverridden(t)).length;
  }

  /** Count of rows that have other (non-duplicate) issues. */
  get otherIssuesCount(): number {
    return this.invalidTransactions.filter(t => !this.isAlreadyExists(t)).length;
  }

  get selectedCount(): number {
    return this.allTransactions.filter(t => t.isImport && this.isReimportable(t)).length;
  }

  get selectedTotal(): number {
    return this.allTransactions
      .filter(t => t.isImport && this.isReimportable(t))
      .reduce((sum, t) => sum + (t.amount || 0), 0);
  }

  get allSelected(): boolean {
    const visible = this.filteredAll.filter(t => this.isReimportable(t));
    return visible.length > 0 && visible.every(t => t.isImport);
  }

  get indeterminate(): boolean {
    const visible = this.filteredAll.filter(t => this.isReimportable(t));
    const sel = visible.filter(t => t.isImport).length;
    return sel > 0 && sel < visible.length;
  }

  toggleAll(checked: boolean): void {
    this.filteredAll.forEach(t => {
      if (this.isReimportable(t)) t.isImport = checked;
    });
  }

  // ── Grouping ───────────────────────────────────────────────

  collapsedGroups = new Set<string>();
  groupedTransactions: { accountName: string; transactions: ImportTransaction[] }[] = [];

  rebuildGroupedTransactions(): void {
    const filtered = this.filteredAll;
    const map = new Map<string, ImportTransaction[]>();
    for (const tx of filtered) {
      const key = tx.accountName || '—';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(tx);
    }
    this.groupedTransactions = Array.from(map.entries()).map(([accountName, transactions]) => ({ accountName, transactions }));

    // Single account → expanded; multiple accounts → all collapsed
    if (this.groupedTransactions.length > 1) {
      this.collapsedGroups = new Set(this.groupedTransactions.map(g => g.accountName));
    } else {
      this.collapsedGroups = new Set();
    }
  }

  trackByGroup(_: number, group: { accountName: string }): string {
    return group.accountName;
  }

  trackByTx(_: number, tx: ImportTransaction): string {
    return `${tx.rowNumber ?? tx.description}-${tx.transactionDate}-${tx.amount}`;
  }

  toggleGroup(accountName: string): void {
    const next = new Set(this.collapsedGroups);
    if (next.has(accountName)) { next.delete(accountName); } else { next.add(accountName); }
    this.collapsedGroups = next;
  }

  isGroupCollapsed(accountName: string): boolean {
    return this.collapsedGroups.has(accountName);
  }

  groupTotal(transactions: ImportTransaction[]): number {
    return transactions.reduce((sum, t) => sum + (t.amount || 0), 0);
  }

  isGroupAllSelected(transactions: ImportTransaction[]): boolean {
    const reimportable = transactions.filter(t => this.isReimportable(t));
    return reimportable.length > 0 && reimportable.every(t => t.isImport);
  }

  isGroupIndeterminate(transactions: ImportTransaction[]): boolean {
    const reimportable = transactions.filter(t => this.isReimportable(t));
    const sel = reimportable.filter(t => t.isImport).length;
    return sel > 0 && sel < reimportable.length;
  }

  toggleGroupAll(transactions: ImportTransaction[], checked: boolean): void {
    transactions.forEach(t => {
      if (this.isReimportable(t)) t.isImport = checked;
    });
  }

  get panelTitle(): string {
    switch (this.step) {
      case 'method': return 'Import Transactions';
      case 'history': return 'Import Transactions';
      case 'bank': return 'Bank Feed';
      case 'xero': return 'Import From Xero';
      case 'spreadsheet': return 'Import From Spreadsheet';
      case 'map-columns': return 'Map Columns';
      case 'map-accounts': return 'Map Accounts';
      case 'review': return this.source === 'Refresh' ? 'Review Refresh' : 'Review Transactions';
      case 'refresh': return 'Refresh From Xero';
    }
  }

  constructor(
    private readonly _filterService: FilterService,
    private readonly _organizationService: OrganizationService,
    private readonly _toasterMessageService: ToasterMessageService,
    private readonly _reportService: UnreconciledReportService,
    private readonly _bankFeedsService: BankFeedsService,
    private readonly _entitlements: EntitlementsService,
    private readonly _dailySync: TransactionDailySyncService,
    private readonly _dailySyncDialog: DailySyncDialogService,
    private readonly _transactionsService: TransactionsService,
  ) {}

  // ── Account rows: the bank or card icon, and a detail line, as Settings > Banking shows them ──

  acctKind(account: PaymentAccount): 'card' | 'bank' {
    return isCreditCardAccountType(account?.bankAccountType) ? 'card' : 'bank';
  }

  /** "Credit card · •••• 1234", or just the kind when there is no number. */
  acctDetail(account: PaymentAccount): string {
    const digits = (account?.bankAccountNumber || '').replace(/\s/g, '');
    return [accountTypeLabel(account?.bankAccountType) || 'Bank account', digits ? `•••• ${digits.slice(-4)}` : '']
      .filter(Boolean).join(' · ');
  }

  // ── Daily sync (Xero) ──────────────────────────────────────

  dailySyncState: DailySyncState | null = null;

  get dailySyncMode(): DailySyncMode {
    return TransactionDailySyncService.mode(this.dailySyncState);
  }

  get dailySyncPillLabel(): string {
    switch (this.dailySyncMode) {
      case 'active': return 'On';
      case 'activate': return 'Off';
      case 'ask-admin': return 'Off';
      default: return 'Paid plans';
    }
  }

  get dailySyncActionLabel(): string {
    return TransactionDailySyncService.actionLabel(this.dailySyncState);
  }

  get dailySyncDescription(): string {
    return TransactionDailySyncService.describe(this.dailySyncState);
  }

  private loadDailySync(): void {
    const org = this.organisationId;
    if (!org || !this.isXeroAccount) { return; }
    this._dailySync.state(org).subscribe(
      state => this.dailySyncState = state,
      () => this.dailySyncState = null);
  }

  async openDailySync(): Promise<void> {
    await this._dailySyncDialog.open(this.organisationId,
      (this.bankAccountList || []).filter(a => !!a.accountID)
        .map(a => ({ accountID: a.accountID, name: a.name, kind: this.acctKind(a), detail: this.acctDetail(a) })));
    this.loadDailySync();
  }

  ngOnInit(): void {
    this.newReport = new NewReport(this._organizationService.selectedOrganization);
    this.dropdownBankSettings = {
      singleSelection: false,
      idField: 'accountID',
      textField: 'name',
      selectAllText: 'Select All',
      unSelectAllText: 'Unselect All',
      itemsShowLimit: 3,
      allowSearchFilter: true
    };
    this._organizationService.organizationList.subscribe(data => {
      if (data) {
        const org = data.find(x =>
          x.organisationID.toLowerCase() === sessionStorage.organizationId.toLowerCase()
        );
        // Compared against the enum rather than assigned raw. accountingIntegration
        // is AccountingIntegration (xero = 0, QBO = 1, None = 2), so assigning it to a
        // boolean happened to work only because xero is the zero member, and the
        // template then had to test `!isXeroAccount` to show the Xero card. Behaviour
        // is unchanged; it now says what it means.
        if (org) {
          this.isXeroAccount = org.accountingIntegration === AccountingIntegration.xero;
          this.isNoAccounting = org.accountingIntegration === AccountingIntegration.None;
          this.loadDailySync();
        }
      }
    });
    this.loadBankAccounts();
    this.loadFinancialSettings();
    this.loadIncompleteReports();

    // SD-1207: only the entitlement is resolved up front, because it decides whether the
    // method tile is rendered at all. Availability and the feed list wait until the tile
    // is clicked -- most visits to this wizard are a spreadsheet upload.
    this._entitlements.has$(MODULES.BANK_FEEDS).subscribe(
      granted => {
        this.hasBankFeeds = granted;
        if (granted) { this.loadBankAvailability(); }
      },
      () => { this.hasBankFeeds = false; }
    );

    if (this.initialStep === 'bank') { this.selectMethod('bank'); }
    if (this.initialStep === 'refresh') { this.startRefresh(); }
  }

  /**
   * The Transactions screen's Refresh: asks Xero what is new and which statuses have changed,
   * then opens the review. Nothing is written until Apply. When there is nothing to review it
   * says so and closes.
   */
  startRefresh(): void {
    const orgId = this.organisationId;
    if (!orgId) { return; }

    this.step = 'refresh';
    this.refreshError = '';
    this.refreshChanges = [];
    this._transactionsService.refreshPreview(orgId).pipe(timeout(REFRESH_TIMEOUT_MS)).subscribe(
      preview => {
        this.refreshAccountCount = preview.accounts.length;
        if (!preview.newTransactions.length && !preview.statusChanges.length) {
          this._toasterMessageService.displayToasterState(ToasterTypes.Success, 'Up to date',
            preview.accounts.length
              ? 'Everything here matches Xero. Nothing new to import.'
              : 'There are no Xero bank accounts here to compare yet. Import one first.');
          this.closed.emit();
          return;
        }

        this.validTransactions = preview.newTransactions.map(line => ({
          ...line,
          importStatus: 'success',
          isImport: true,
          isReconciled: (line.reconciled || '').toLowerCase() === 'yes',
          validationErrors: []
        }));
        this.invalidTransactions = [];
        // Ticked unless Synced's own work stops it; removals start unticked, being the one
        // change that cannot be taken back.
        this.refreshChanges = preview.statusChanges.map(c => ({
          ...c,
          selected: c.canApply && c.change !== 'Missing'
        }));
        this.source = 'Refresh';
        this.selectedReport = null;
        this.searchQuery = '';
        this.statusFilter = 'all';
        this.step = 'review';
        this.rebuildAllTransactions();
      },
      error => {
        this.refreshError = error && error.name === 'TimeoutError'
          ? 'Xero is taking too long to answer. Try again in a minute.'
          : (error && error.error && error.error.message) || (error && error.message) || 'We could not check Xero for changes.';
      });
  }

  /** The status changes, grouped as the review lists them. */
  get refreshChangeGroups(): { change: string; title: string; hint: string; rows: RefreshChangeRow[] }[] {
    const groups = [
      { change: 'Reconciled', title: 'Now reconciled in Xero',
        hint: 'Reconciled in Xero since they came in. They will be marked reconciled here.' },
      { change: 'Unreconciled', title: 'No longer reconciled in Xero',
        hint: 'Reconciled here, but not in Xero any more. They will go back to reconcile.' },
      { change: 'Missing', title: 'Not in Xero any more',
        hint: 'Imported from Xero, but no longer on its bank statement. Tick the ones to remove from Transactions.' }
    ];
    return groups
      .map(g => ({ ...g, rows: this.refreshChanges.filter(c => c.change === g.change) }))
      .filter(g => g.rows.length > 0);
  }

  get selectedChangeCount(): number {
    return this.refreshChanges.filter(c => c.selected && c.canApply).length;
  }

  isChangeGroupAllSelected(rows: RefreshChangeRow[]): boolean {
    const open = rows.filter(r => r.canApply);
    return open.length > 0 && open.every(r => r.selected);
  }

  isChangeGroupIndeterminate(rows: RefreshChangeRow[]): boolean {
    const open = rows.filter(r => r.canApply);
    const n = open.filter(r => r.selected).length;
    return n > 0 && n < open.length;
  }

  toggleChangeGroup(rows: RefreshChangeRow[], checked: boolean): void {
    rows.forEach(r => { if (r.canApply) { r.selected = checked; } });
  }

  trackByChange(_: number, row: RefreshChangeRow): string {
    return row.unreconciledReportId;
  }

  /** What the Apply button says on a Refresh: everything ticked, new and changed. */
  get refreshApplyLabel(): string {
    const n = this.selectedCount + this.selectedChangeCount;
    return `Apply ${n} change${n !== 1 ? 's' : ''}`;
  }

  /**
   * Applies a reviewed Refresh: the ticked new transactions (filed in the report chosen, if
   * any) and the ticked status changes, as one run in the import history.
   */
  private async confirmRefresh(selectedTx: ImportTransaction[], reportId: string): Promise<void> {
    const orgId = this.organisationId;
    if (!orgId) { return; }

    this.isSaving = true;
    try {
      const result = await this._transactionsService.refreshApply({
        organizationId: orgId,
        reportId: reportId || null,
        newTransactions: selectedTx,
        changes: this.refreshChanges
          .filter(c => c.selected && c.canApply)
          .map(c => ({ unreconciledReportId: c.unreconciledReportId, change: c.change }))
      }).toPromise();
      this.isSaving = false;

      const parts: string[] = [];
      if (result.insertedCount) { parts.push(`${result.insertedCount} imported`); }
      if (result.reconciledCount) { parts.push(`${result.reconciledCount} marked reconciled`); }
      if (result.unreconciledCount) { parts.push(`${result.unreconciledCount} back to reconcile`); }
      if (result.removedCount) { parts.push(`${result.removedCount} removed`); }
      if (result.droppedCount) { parts.push(`${result.droppedCount} already here`); }
      // Coded in Synced between the preview and now: left alone, and worth saying.
      if (result.skippedCount) { parts.push(`${result.skippedCount} left as they were`); }
      this._toasterMessageService.displayToasterState(
        ToasterTypes.Success, 'Refreshed', parts.length ? parts.join(', ') + '.' : 'Nothing changed.');
      this.importComplete.emit('refresh');
    } catch (error) {
      this.isSaving = false;
      this._toasterMessageService.displayToasterState(
        ToasterTypes.Error, 'Error', (error && error.message) || 'We could not apply the refresh.');
    }
  }

  /**
   * Pulls FinancialYearEndMonth / FinancialYearEndDay / lock dates from
   * dbo.Organisations so period pills compute the correct ranges. Only after the
   * settings load do we apply the default "This Month" period — that way the
   * date-range pill never flashes the wrong values for FY-based selections.
   */
  private loadFinancialSettings(): void {
    const orgId = sessionStorage.organizationId || this._organizationService.selectedOrganization;
    if (!orgId) {
      this.onPeriodSelect('This Month');
      return;
    }
    this._organizationService.getOrganisationFinancialSettings(orgId).subscribe(
      (resp: any) => {
        const data = resp?.data ?? resp?.Data ?? null;
        if (data) {
          this.financialYearEndDay = data.financialYearEndDay ?? data.FinancialYearEndDay ?? null;
          this.financialYearEndMonth = data.financialYearEndMonth ?? data.FinancialYearEndMonth ?? null;
          const eoy = data.endOfYearLockDate ?? data.EndOfYearLockDate ?? null;
          const period = data.periodLockDate ?? data.PeriodLockDate ?? null;
          this.endOfYearLockDate = eoy ? new Date(eoy) : null;
          this.periodLockDate = period ? new Date(period) : null;
        }
        this.onPeriodSelect('This Month');
      },
      () => {
        this.onPeriodSelect('This Month');
      }
    );
  }

  private loadIncompleteReports(): void {
    const orgId = sessionStorage.organizationId || this._organizationService.selectedOrganization;
    if (!orgId) return;
    this._reportService.getIncompleteUnreconciledReports(orgId).subscribe(
      (resp: any) => {
        const data = resp?.data ?? resp?.Data ?? [];
        this.incompleteReports = (data || []).map((r: any) => ({
          id: r.id ?? r.ID,
          reportName: r.reportName ?? r.ReportName,
          status: 'open',
        }));
      },
      () => { /* fall back to parent-supplied reports list */ }
    );
  }

  ngOnDestroy(): void {}

  // ── Navigation ──────────────────────────────────────────────

  selectMethod(method: 'bank' | 'xero' | 'spreadsheet'): void {
    this.step = method;

    // SD-1207: the feed list is fetched on entry rather than on load, because most
    // visits to this wizard never touch it.
    if (method === 'bank') { this.loadBankFeeds(); }
  }

  goBack(): void {
    // A Refresh has no step before its review: back is the Import tab.
    if (this.step === 'refresh' || (this.step === 'review' && this.source === 'Refresh')) {
      this.step = 'method';
      this.source = 'Spreadsheet';
      this.refreshChanges = [];
      this.validTransactions = [];
      this.invalidTransactions = [];
      this.rebuildAllTransactions();
      this.searchQuery = '';
      this.statusFilter = 'all';
      return;
    }
    if (this.step === 'review') {
      this.step = this.source === 'Report' ? 'xero' : this.source === 'BankFeed' ? 'bank' : 'spreadsheet';
      this.reviewFeed = null;
      this.bankPreviewChangeCount = 0;
      this.validTransactions = [];
      this.invalidTransactions = [];
      this.rebuildAllTransactions();
      this.searchQuery = '';
      this.statusFilter = 'all';
    } else if (this.step === 'map-accounts') {
      this.step = 'spreadsheet';
    } else if (this.step === 'map-columns') {
      this.step = 'spreadsheet';
    } else {
      this.step = 'method';
      this.fileObj = null;
    }
  }

  close(): void {
    this.closed.emit();
  }

  // ── Bank Accounts ─────────────────────────────────────────

  // ── SD-1207 Bank feed ───────────────────────────────────────

  // Public: the History tab passes it to <app-import-history>.
  get organisationId(): string {
    return sessionStorage.organizationId || this._organizationService.selectedOrganization;
  }

  /** The Import / History tabs belong to the drawer's first screen, not the wizard's steps. */
  get showTabs(): boolean {
    return this.step === 'method' || this.step === 'history';
  }

  /**
   * Whether bank feeds can actually be used here. Kept separate from the entitlement so
   * an entitled organisation that is not US gets a DISABLED tile carrying the reason,
   * rather than a feature that silently is not there.
   */
  private loadBankAvailability(): void {
    const orgId = this.organisationId;
    if (!orgId) {
      // Without an organisation there is nothing to check, and leaving the flag on would
      // strand the tile on "Checking..." forever.
      this.bankAvailability = {
        available: false,
        reason: 'We could not work out which organisation you are in.'
      };
      return;
    }

    this.bankAvailabilityChecking = true;

    this._bankFeedsService.getAvailability(orgId).subscribe(
      availability => {
        this.bankAvailabilityChecking = false;
        this.bankAvailability = availability || {
          available: false,
          reason: 'We could not check whether bank feeds are available.'
        };
      },
      error => {
        this.bankAvailabilityChecking = false;
        this.bankAvailability = {
          available: false,
          reason: this.readError(error, 'We could not check whether bank feeds are available.')
        };
      }
    );
  }

  get isBankFeedAvailable(): boolean {
    return this.hasBankFeeds && !!this.bankAvailability && this.bankAvailability.available;
  }

  /**
   * The line under "Connect a bank account". ALWAYS returns something.
   *
   * It used to return '' while the availability call was in flight, which -- combined with
   * the tile being disabled until the answer arrived -- rendered a card with a title, no
   * description, no chevron and no click. A dead control with no explanation is worse than
   * either a working one or an honestly disabled one.
   */
  get bankFeedDescription(): string {
    if (this.bankAvailabilityChecking) { return 'Checking availability\u2026'; }
    if (this.isBankFeedAvailable) { return 'Transactions arrive automatically, every day'; }
    if (this.bankAvailability && this.bankAvailability.reason) { return this.bankAvailability.reason; }
    return 'Bank feeds are not available for this organisation.';
  }

  loadBankFeeds(): void {
    const orgId = this.organisationId;
    if (!orgId) { return; }

    this.bankLoading = true;
    this.bankError = '';

    this._bankFeedsService.getAccounts(orgId).subscribe(
      feeds => {
        this.bankFeeds = feeds || [];
        this.bankLoading = false;
        // Pre-select what each feed is already mapped to, so the dropdown reflects
        // reality rather than reading as unmapped.
        this.bankFeeds.forEach(feed => {
          if (feed.accountId) { this.bankAccountChoice[feed.id] = feed.accountId; }
        });
      },
      error => {
        this.bankLoading = false;
        this.bankError = this.readError(error, 'We could not load your bank feeds.');
      }
    );
  }

  /**
   * Opens Stripe's authentication flow. `relinkConnectionId` repairs a feed Stripe has
   * marked inactive instead of linking a new one.
   */
  async connectBank(relinkConnectionId?: string): Promise<void> {
    const orgId = this.organisationId;
    if (!orgId || this.bankBusy) { return; }

    this.bankBusy = true;
    this.bankError = '';

    try {
      const feeds = await this._bankFeedsService.connect(orgId, relinkConnectionId);

      // null means the user closed Stripe's modal without linking anything. That is a
      // decision, not a failure, and showing an error for it is worse than silence.
      if (feeds) {
        this.bankFeeds = feeds;
        this.bankFeeds.forEach(feed => {
          if (feed.accountId) { this.bankAccountChoice[feed.id] = feed.accountId; }
        });

        const unmapped = this.bankFeeds.filter(f => !f.isMapped).length;
        this._toasterMessageService.displayToasterState(
          ToasterTypes.Success,
          'Bank connected',
          unmapped
            ? 'Now choose which account each feed belongs to.'
            : 'Your bank feed is connected.');
      }
    } catch (error) {
      this.bankError = this.readError(error, 'We could not connect that bank.');
    } finally {
      this.bankBusy = false;
    }
  }

  /** Binds a feed to a ledger account. The server also turns on Stripe's daily refresh. */
  mapBankFeed(feed: BankFeedAccount): void {
    const orgId = this.organisationId;
    const accountId = this.bankAccountChoice[feed.id];
    if (!orgId || !accountId || this.bankBusy) { return; }

    this.bankBusy = true;
    this.bankError = '';

    this._bankFeedsService.mapAccount(orgId, feed.id, accountId).subscribe(
      updated => {
        this.bankBusy = false;
        const index = this.bankFeeds.findIndex(f => f.id === feed.id);
        if (index >= 0 && updated) { this.bankFeeds[index] = updated; }
        this._toasterMessageService.displayToasterState(
          ToasterTypes.Success, 'Account linked',
          'This feed will now update daily. Import history below.');
      },
      error => {
        this.bankBusy = false;
        this.bankError = this.readError(error, 'We could not link that feed to the account.');
      }
    );
  }

  /** What "Create account" will call the new account. */
  suggestedAccountName(feed: BankFeedAccount): string {
    return BankFeedsService.suggestedAccountName(feed);
  }

  /**
   * For a bank with no account in Synced yet: the server creates one from the feed's own
   * details and links the two in the same call.
   */
  createAccountForFeed(feed: BankFeedAccount): void {
    const orgId = this.organisationId;
    if (!orgId || this.bankBusy) { return; }

    this.bankBusy = true;
    this.bankError = '';

    this._bankFeedsService.createAndMapAccount(orgId, feed.id).subscribe(
      updated => {
        this.bankBusy = false;
        const index = this.bankFeeds.findIndex(f => f.id === feed.id);
        if (index >= 0 && updated) { this.bankFeeds[index] = updated; }
        this.loadBankAccounts();
        this._toasterMessageService.displayToasterState(
          ToasterTypes.Success, 'Account created',
          'This feed will now update daily. Import history below.');
      },
      error => {
        this.bankBusy = false;
        this.bankError = this.readError(error, 'We could not create an account for that feed.');
        // The account is created before the feed is linked, so it may exist even though
        // the link failed. Reloading lets it be chosen from the list instead.
        this.loadBankAccounts();
      }
    );
  }

  /** A feed that can be imported from: linked to an account, and still shared by the bank. */
  canReviewFeed(feed: BankFeedAccount): boolean {
    return !!feed && feed.isMapped && feed.health !== 'needs_reconnect';
  }

  /**
   * Opens the review step for one feed, the same step the spreadsheet and Xero imports end
   * in: what Stripe holds from the chosen date, new transactions ticked, ones this feed
   * already imported shown and locked. Nothing is written until the user confirms. The
   * start date is clamped server-side to what Stripe actually holds -- about 180 days,
   * institution-dependent -- so asking for more is not an error.
   */
  reviewBankFeed(feed: BankFeedAccount): void {
    const orgId = this.organisationId;
    if (!orgId || this.bankBusy || !this.canReviewFeed(feed)) { return; }

    this.bankBusy = true;
    this.bankReviewingId = feed.id;
    this.bankError = '';
    this.bankImportResult = null;

    const from = this.bankImportFrom ? new Date(this.bankImportFrom) : null;

    this._bankFeedsService.previewHistory(orgId, feed.id, from).subscribe(
      preview => {
        this.bankBusy = false;
        this.bankReviewingId = '';

        const rows = (preview && preview.transactions) || [];
        const since = preview && preview.from ? moment(preview.from).format('D MMM YYYY') : '';

        if (!rows.some(r => !r.alreadyImported)) {
          this._toasterMessageService.displayToasterState(
            ToasterTypes.Info, 'Nothing new to import',
            rows.length
              ? `Everything from this account since ${since} is already imported.`
              : `The bank has no transactions for this account since ${since}.`);
          return;
        }

        const accountName = feed.accountName || this.suggestedAccountName(feed);
        const toRow = (t: BankFeedPreviewTransaction): ImportTransaction => ({
          description: t.description,
          amount: t.amount,
          transactionDate: t.transactionDate,
          accountName,
          // 'success' is what the review step treats as a clean row; "exists" locks one.
          importStatus: t.alreadyImported ? 'Already Exists' : 'success',
          isImport: !t.alreadyImported,
          bankTransactionId: t.id,
          validationErrors: []
        });

        this.reviewFeed = feed;
        this.bankPreviewChangeCount = (preview.updateCount || 0) + (preview.withdrawCount || 0);
        this.validTransactions = rows.filter(r => !r.alreadyImported).map(toRow);
        this.invalidTransactions = rows.filter(r => r.alreadyImported).map(toRow);
        this.source = 'BankFeed';
        this.searchQuery = '';
        this.statusFilter = 'all';
        this.step = 'review';
        this.rebuildAllTransactions();
      },
      error => {
        this.bankBusy = false;
        this.bankReviewingId = '';
        this.bankError = this.readError(error, 'We could not load transactions from that bank feed.');
      }
    );
  }

  /**
   * The review step's Import, for a bank feed: brings in the ticked transactions, filed into
   * the chosen report. Same date as the preview, so the server sees the same transactions.
   */
  private async confirmBankFeedImport(selected: ImportTransaction[], reportId: string): Promise<void> {
    const orgId = this.organisationId;
    const feed = this.reviewFeed;
    if (!orgId || !feed) { return; }

    const from = this.bankImportFrom ? new Date(this.bankImportFrom) : null;
    const ids = selected.map(t => t.bankTransactionId).filter(Boolean);

    this.isSaving = true;
    try {
      const result = await this._bankFeedsService
        .importHistory(orgId, feed.id, from, ids, reportId)
        .toPromise();
      this.isSaving = false;
      this.bankImportResult = result;

      this._toasterMessageService.displayToasterState(
        ToasterTypes.Success, 'Import finished', this.describeImport(result));

      // Same signal the spreadsheet and Xero paths emit, so the ledger behind the drawer reloads.
      this.importComplete.emit('bankfeed');
    } catch (error) {
      this.isSaving = false;
      this._toasterMessageService.displayToasterState(
        ToasterTypes.Error, 'Error', this.readError(error, 'We could not import from that bank feed.'));
    }
  }

  /** A sentence about what an import did, rather than six numbers. */
  describeImport(result: BankFeedImportResult): string {
    if (!result) { return 'Nothing to import.'; }

    const parts: string[] = [];
    if (result.insertedCount) { parts.push(`${result.insertedCount} imported`); }
    if (result.updatedCount) { parts.push(`${result.updatedCount} updated`); }
    if (result.duplicateCount) { parts.push(`${result.duplicateCount} already here`); }
    if (result.voidedCount) { parts.push(`${result.voidedCount} withdrawn`); }
    // Worth its own clause: it means the bank and the books now disagree on a row
    // somebody has already signed off.
    if (result.conflictCount) { parts.push(`${result.conflictCount} changed at the bank but already reconciled`); }
    if (result.unmappedCount) { parts.push(`${result.unmappedCount} had no matching account`); }

    return parts.length ? parts.join(', ') + '.' : 'No new transactions.';
  }

  /** The user-facing label for a feed's state. */
  bankHealthLabel(feed: BankFeedAccount): string {
    switch (feed && feed.health) {
      case 'ok': return 'Connected';
      case 'needs_account': return 'Choose an account';
      case 'needs_reconnect': return 'Reconnect needed';
      case 'not_subscribed': return 'Not updating';
      case 'removed': return 'Disconnected';
      default: return '';
    }
  }

  /**
   * Errors arrive two ways: an Error thrown by BankFeedsService when the server answered
   * status 0 with a message written for the user, and an HttpErrorResponse when it did
   * not answer at all. The first is worth showing verbatim; the second is not.
   */
  private readError(error: any, fallback: string): string {
    return BankFeedsService.describeError(error, fallback);
  }

  loadBankAccounts(): void {
    this._organizationService.GetBankAccountDetails(
      'Organisation/GetBankAccountDetails',
      sessionStorage.organizationId
    ).subscribe((response: any) => {
      if (response.status === 0) this.bankAccountList = response.data || [];
    }, () => {
      this.bankAccountList = [];
    });
  }

  onBankItemSelect(): void {
    this.filter.accounts = this.selectedBankAccounts.map(a => a.accountID.replace('&', '%26'));
  }

  onBankDeselect(): void {
    this.filter.accounts = this.selectedBankAccounts.map(a => a.accountID.replace('&', '%26'));
  }

  onBankSelectAll(items: PaymentAccount[]): void {
    this.selectedBankAccounts = items;
    this.filter.accounts = items.map(a => a.accountID.replace('&', '%26'));
  }

  // ── Custom bank account dropdown helpers ──────────────────

  get filteredBankAccounts(): PaymentAccount[] {
    if (!this.bankSearch) return this.bankAccountList;
    const q = this.bankSearch.toLowerCase();
    return this.bankAccountList.filter(a => a.name?.toLowerCase().includes(q));
  }

  get allBankAccountsSelected(): boolean {
    return this.bankAccountList.length > 0 &&
      this.bankAccountList.every(a => this.isAccountSelected(a));
  }

  get someBankAccountsSelected(): boolean {
    return this.selectedBankAccounts.length > 0 && !this.allBankAccountsSelected;
  }

  isAccountSelected(acct: PaymentAccount): boolean {
    return this.selectedBankAccounts.some(a => a.accountID === acct.accountID);
  }

  toggleAccount(acct: PaymentAccount, checked: boolean): void {
    if (checked) {
      if (!this.isAccountSelected(acct)) this.selectedBankAccounts = [...this.selectedBankAccounts, acct];
    } else {
      this.selectedBankAccounts = this.selectedBankAccounts.filter(a => a.accountID !== acct.accountID);
    }
    this.filter.accounts = this.selectedBankAccounts.map(a => a.accountID.replace('&', '%26'));
  }

  toggleAllBankAccounts(checked: boolean): void {
    this.selectedBankAccounts = checked ? [...this.bankAccountList] : [];
    this.filter.accounts = this.selectedBankAccounts.map(a => a.accountID.replace('&', '%26'));
  }

  deselectAccount(acct: PaymentAccount): void {
    this.selectedBankAccounts = this.selectedBankAccounts.filter(a => a.accountID !== acct.accountID);
    this.filter.accounts = this.selectedBankAccounts.map(a => a.accountID.replace('&', '%26'));
  }

  // ── Date period helpers ────────────────────────────────────

  periodOptions(): string[] {
    return Object.values(this.unreconciledreportperiod);
  }

  onPeriodSelect(period: string): void {
    this.dateRangeSelected = period;
    this.showCustomFilter = period === 'Custom Range';
    if (this.showCustomFilter) {
      // Seed the custom inputs with the previously-shown range so the user has a sensible starting point.
      if (!this.customStartDate) this.customStartDate = moment(this.startDate).format('YYYY-MM-DD');
      if (!this.customEndDate) this.customEndDate = moment(this.lastDayOfMonth).format('YYYY-MM-DD');
      this.applyCustomRange();
      return;
    }
    this.recalcDateRange();
  }

  /** The organisation's fiscal calendar, in the shape the shared resolver takes. */
  private get financialYearEnd(): FinancialYearEnd {
    return { month: this.financialYearEndMonth, day: this.financialYearEndDay };
  }

  /**
   * Returns the moment format string the org has chosen for date displays
   * (en-US → MM/DD/YYYY, otherwise DD/MM/YYYY). Mirrors the rule used by the
   * shared `formatedDate` pipe so the pill matches the rest of the app.
   */
  private get displayDateFormat(): string {
    return this._organizationService.selectedOrganizationDateFormat === 'en-US'
      ? 'MM/DD/YYYY'
      : 'DD/MM/YYYY';
  }

  /**
   * Renders a transaction date in the org's preferred date format. The API
   * returns dates as raw strings (could be ISO from Xero or DD/MM/YYYY from a
   * spreadsheet), so we parse loosely and reformat. Falls back to the original
   * string if no known format matches.
   */
  displayTransactionDate(value: string): string {
    if (!value) return '—';
    const m = moment(value, [
      moment.ISO_8601,
      'YYYY-MM-DDTHH:mm:ss',
      'YYYY-MM-DD',
      'DD/MM/YYYY',
      'MM/DD/YYYY',
      'D/M/YYYY',
      'D/MM/YYYY',
    ], true);
    return m.isValid() ? m.format(this.displayDateFormat) : value;
  }

  recalcDateRange(): void {
    // The period maths lives in core/util/report-period so the Transactions ledger's
    // date-range modal and this wizard cannot drift apart on financial-year edges.
    // This wizard never offers All Dates, so a null range is not reachable here;
    // fall back to This Month if one ever arrives.
    const range = resolveReportPeriod(this.dateRangeSelected, this.financialYearEnd)
      ?? resolveReportPeriod(ReportPeriod.ThisMonth, this.financialYearEnd);

    this.startDate = range.start;
    this.lastDayOfMonth = range.end;

    const fmt = this.displayDateFormat;
    this.showDateRange =
      moment(this.startDate).format(fmt) + ' to ' +
      moment(this.lastDayOfMonth).format(fmt);
  }

  /** Applies user-typed custom range, validating that start <= end. */
  applyCustomRange(): void {
    if (!this.customStartDate || !this.customEndDate) {
      this.customRangeError = '';
      this.showDateRange = '';
      return;
    }
    const start = moment(this.customStartDate, 'YYYY-MM-DD', true);
    const end = moment(this.customEndDate, 'YYYY-MM-DD', true);
    if (!start.isValid() || !end.isValid()) {
      this.customRangeError = 'Please enter valid dates.';
      return;
    }
    if (start.isAfter(end)) {
      this.customRangeError = 'Start date must be on or before end date.';
      return;
    }
    this.customRangeError = '';
    this.startDate = start.toDate();
    this.lastDayOfMonth = end.toDate();
    const fmt = this.displayDateFormat;
    this.showDateRange = start.format(fmt) + ' to ' + end.format(fmt);
  }

  /**
   * Opens the native date picker when the user clicks anywhere on a
   * `<input type="date">`. Without this, Chrome only opens the picker when
   * the calendar icon itself is clicked, which makes the input feel broken.
   */
  openNativeDatePicker(event: Event): void {
    const input = event.target as HTMLInputElement & { showPicker?: () => void };
    if (typeof input?.showPicker === 'function') {
      try { input.showPicker(); } catch { /* showPicker throws if already open */ }
    }
  }

  // ── Xero import ────────────────────────────────────────────

  /**
   * The chosen period, read as a person would: "1 – 31 Oct 2026", or "Oct 1 – 31, 2026" for an
   * en-US organisation. Empty while a custom range is incomplete or wrong.
   */
  get xeroRangeLabel(): string {
    if (this.customRangeError || (this.showCustomFilter && !this.showDateRange)) { return ''; }
    const start = moment(this.startDate);
    const end = moment(this.lastDayOfMonth);
    if (!start.isValid() || !end.isValid()) { return ''; }

    const us = this._organizationService.selectedOrganizationDateFormat === 'en-US';
    if (start.isSame(end, 'day')) { return end.format(us ? 'MMM D, YYYY' : 'D MMM YYYY'); }
    if (start.isSame(end, 'month')) {
      return us ? `${start.format('MMM D')} – ${end.format('D, YYYY')}` : `${start.format('D')} – ${end.format('D MMM YYYY')}`;
    }
    if (start.isSame(end, 'year')) {
      return us ? `${start.format('MMM D')} – ${end.format('MMM D, YYYY')}` : `${start.format('D MMM')} – ${end.format('D MMM YYYY')}`;
    }
    const full = us ? 'MMM D, YYYY' : 'D MMM YYYY';
    return `${start.format(full)} – ${end.format(full)}`;
  }

  /** Above the Fetch button: what it will ask Xero for. */
  get xeroAccountsSummary(): string {
    const n = this.selectedBankAccounts.length;
    if (!n) { return 'Choose at least one bank account'; }
    return n === 1 ? this.selectedBankAccounts[0].name : `${n} bank accounts`;
  }

  /**
   * The server fetches only the accounts named, so none would come back empty -- and read as
   * "No transactions found" rather than a missing choice.
   */
  get canFetchFromXero(): boolean {
    return !this.isXeroLoading && this.selectedBankAccounts.length > 0 && !!this.xeroRangeLabel;
  }

  importFromXero(): void {
    const bankPart = this.filter.accounts?.length > 0
      ? this.filter.accounts.map(s => `${s},`).join('').slice(0, -1)
      : '';

    if (!this.filter.accounts?.length && !(this.filter.invoiceDateRange?.[0])) {
      this._toasterMessageService.displayToasterState(
        ToasterTypes.Warning, 'Warning', 'Please select at least one bank account or date range.'
      );
      return;
    }

    this.isXeroLoading = true;
    // ISO 8601. The API binds these to DateTime? with System.Text.Json, which accepts
    // only ISO -- 'MM/DD/YYYY' threw, and because the app does not use [ApiController]
    // the whole body then arrived null and the action answered "Report Information
    // Required !" with HTTP 200. That surfaced here as "No transactions found", so a
    // Xero import looked empty rather than broken. Note the invalid-date fallbacks
    // below were always ISO, which is why only the real dates failed.
    const startDate = moment(this.startDate).format('YYYY-MM-DD');
    const endDate = moment(this.lastDayOfMonth).format('YYYY-MM-DD');

    const reportDataModel = {
      organizationId: sessionStorage.organizationId,
      userid: Number(sessionStorage.UserId),
      id: sessionStorage.Id,
      startDate: startDate === 'Invalid date' ? '1900-01-01' : startDate,
      endDate: endDate === 'Invalid date' ? '2048-01-01' : endDate,
      isSavedReport: false,
      isoutstandingReport: false,
      isSavedReportData: false,
      Source: 'XeroDataCheck',
      reportId: undefined,
      Account: bankPart
    };

    this._organizationService.GetUnreconciledReportList(reportDataModel).subscribe(
      data => {
        this.isXeroLoading = false;
        this.source = 'Report';
        if(!data.data || data.data.length === 0) {
          // The server says "No Records found" for an empty statement; anything else is a failure.
          if (data.status === 0 && data.message && data.message !== 'No Records found') {
            this._toasterMessageService.displayToasterState(ToasterTypes.Error, 'Error', data.message);
            return;
          }
          this._toasterMessageService.displayToasterState(
            ToasterTypes.Info, 'No Transactions', 'No transactions found for the selected accounts and date range.');
          return;
        }
        this.parseTransactions(data.data);
        this.step = 'review';
      },
      () => {
        this.isXeroLoading = false;
        this._toasterMessageService.displayToasterState(
          ToasterTypes.Error, 'Error', 'Failed to fetch Xero transactions.'
        );
      }
    );
  }

  // ── Spreadsheet upload ─────────────────────────────────────

  onFileDropped(uploadedFiles: NgxFileDropEntry[]): void {
    const fileEntry = uploadedFiles[0]?.fileEntry.isFile
      ? uploadedFiles[0].fileEntry as FileSystemFileEntry
      : null;
    fileEntry?.file((f: File) => {
      this.fileObj = { name: f.name, file: f, size: f.size / (1024 * 1024) };
      this.uploadError = null;
    });
  }

  onFileSelected(event: Event): void {
    const [f] = Array.from((event.target as HTMLInputElement).files as FileList);
    this.fileObj = { name: f.name, file: f, size: f.size / (1024 * 1024) };
    this.uploadError = null;
  }

  removeFile(): void {
    this.fileObj = null;
    this.uploadError = null;
  }

  private static readonly REQUIRED_COLUMNS: { key: string; display: string }[] = [
    { key: 'date', display: 'DATE' },
    { key: 'description', display: 'DESCRIPTION' },
    { key: 'spent', display: 'SPENT' },
    { key: 'received', display: 'RECEIVED' },
    { key: 'account name', display: 'Account Name' },
  ];

  private readCsvText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Unable to read the file.'));
      reader.readAsText(file);
    });
  }

  async processSpreadsheet(): Promise<void> {
    if (!this.fileObj) return;
    this.uploadError = null;

    // Read CSV and extract headers
    try {
      this.csvText = await this.readCsvText(this.fileObj.file);
    } catch {
      this.uploadError = 'Unable to read the file.';
      return;
    }

    const firstLine = this.csvText.split(/\r?\n/)[0];
    if (!firstLine?.trim()) {
      this.uploadError = 'The file appears to be empty.';
      return;
    }

    this.csvHeaders = firstLine.split(',').map(h => h.trim().replace(/['"]/g, ''));
    const headersLower = this.csvHeaders.map(h => h.toLowerCase());

    // Auto-map exact matches, flag anything missing
    const mappings: ColumnMapping[] = [];
    let hasMissing = false;
    for (const col of ImportTransactionsComponent.REQUIRED_COLUMNS) {
      const idx = headersLower.indexOf(col.key);
      if (idx >= 0) {
        mappings.push({ required: col.display, requiredKey: col.key, mapped: this.csvHeaders[idx] });
      } else {
        hasMissing = true;
        mappings.push({ required: col.display, requiredKey: col.key, mapped: null });
      }
    }

    if (hasMissing) {
      this.columnMappings = mappings;
      this.step = 'map-columns';
      return;
    }

    // Headers match — upload directly
    await this.uploadFile(this.fileObj.file);
  }

  /** Columns the user hasn't mapped to a required column yet */
  get unmappedFileHeaders(): string[] {
    const used = new Set(this.columnMappings.map(m => m.mapped).filter(Boolean));
    return this.csvHeaders.filter(h => !used.has(h));
  }

  isHeaderTaken(header: string, current: ColumnMapping): boolean {
    return current.mapped !== header && this.columnMappings.some(m => m !== current && m.mapped === header);
  }

  get allColumnsMapped(): boolean {
    return this.columnMappings.every(m => m.mapped !== null);
  }

  async applyColumnMappingAndUpload(): Promise<void> {
    if (!this.allColumnsMapped) return;
    this.uploadError = null;

    // Build a mapping of file-column-index → required header name
    const lines = this.csvText.split(/\r?\n/);
    const fileHeaders = lines[0].split(',').map(h => h.trim().replace(/['"]/g, ''));

    // Create header rename map: original header → required display name
    const renameMap = new Map<string, string>();
    for (const m of this.columnMappings) {
      renameMap.set(m.mapped!, m.required);
    }

    // Rewrite the header row
    const newHeaders = fileHeaders.map(h => renameMap.get(h) ?? h);
    lines[0] = newHeaders.join(',');
    const newCsvText = lines.join('\n');

    const blob = new Blob([newCsvText], { type: 'text/csv' });
    const file = new File([blob], this.fileObj.name, { type: 'text/csv' });

    await this.uploadFile(file);
  }

  private async uploadFile(file: File): Promise<void> {
    this.isFileUploading = true;

    try {
      const uploadPromise = this._filterService.uploadExcel({
        organisationId: this._organizationService.selectedOrganization,
        invoice: file,
        recordId: '',
        isBankTransMode: true
      } as any);

      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('timeout')), 60000)
      );

      const data: any = await Promise.race([uploadPromise, timeoutPromise]);

      this.isFileUploading = false;

      if (data.status === 1) {
        this.source = 'Spreadsheet';
        this.parseTransactions(data.data);
        this.checkAccountMatches();
      } else {
        const errMsg = data.message || (typeof data.data === 'string' ? data.data : null);
        this.uploadError = errMsg || 'Failed to process spreadsheet. Please check your file and try again.';
        this.step = 'spreadsheet';
      }
    } catch (err: any) {
      this.isFileUploading = false;
      this.uploadError = err?.message === 'timeout'
        ? 'Upload timed out. The file may be too large or the server is not responding. Please try again.'
        : 'Unable to upload file. Please check your connection and try again.';
      this.step = 'spreadsheet';
    }
  }

  // ── Account mapping ───────────────────────────────────────────

  private checkAccountMatches(): void {
    const orgNames = new Set(this.bankAccountList.map(a => a.name?.toLowerCase()));
    const uniqueCsvAccounts = [...new Set(this.allTransactions.map(t => t.accountName).filter(Boolean))];
    const unmatched = uniqueCsvAccounts.filter(name => !orgNames.has(name.toLowerCase()));

    if (unmatched.length > 0) {
      this.accountMappings = unmatched.map(csvName => ({ csvName, mappedAccountName: '' }));
      this.step = 'map-accounts';
    } else {
      this.step = 'review';
    }
  }

  get allAccountsMapped(): boolean {
    return this.accountMappings.every(m => !!m.mappedAccountName);
  }

  applyAccountMappingAndReview(): void {
    // Build lookup: csv account name → org account name
    const map = new Map<string, string>();
    for (const m of this.accountMappings) {
      if (m.mappedAccountName) {
        map.set(m.csvName.toLowerCase(), m.mappedAccountName);
      }
    }

    // Update transaction account names and clear bank-account errors
    for (const tx of this.allTransactions) {
      const mapped = map.get(tx.accountName?.toLowerCase());
      if (mapped) {
        tx.accountName = mapped;

        // Remove "Bank Account Missing" validation errors now that account is mapped
        if (tx.validationErrors?.length) {
          tx.validationErrors = tx.validationErrors.filter(
            e => !e.message?.toLowerCase().includes('bank account missing')
          );
        }

        // If that was the only error, promote to valid
        const statusLower = tx.importStatus?.toLowerCase() || '';
        if (statusLower.includes('bank account missing') ||
            (tx.validationErrors?.length === 0 && statusLower !== 'success')) {
          tx.importStatus = 'success';
          tx.isImport = true;
          tx.validationErrors = [];
        }
      }
    }

    // Move newly-valid transactions from invalid → valid list
    const nowValid = this.invalidTransactions.filter(t => t.importStatus === 'success');
    if (nowValid.length > 0) {
      this.invalidTransactions = this.invalidTransactions.filter(t => t.importStatus !== 'success');
      this.validTransactions = [...this.validTransactions, ...nowValid];
    }

    this.rebuildAllTransactions();
    this.step = 'review';
  }

  // ── Transaction parsing ────────────────────────────────────

  private parseTransactions(raw: any[]): void {
    this.validTransactions = [];
    this.invalidTransactions = [];

    raw.forEach(item => {
      const tx: ImportTransaction = {
        ...item,
        isImport: item.importStatus === 'success',
        validationErrors: item.validationErrors || this.buildValidationErrors(item)
      };

      if (item.importStatus === 'success') {
        this.validTransactions.push(tx);
      } else {
        this.invalidTransactions.push(tx);
      }
    });

    this.rebuildAllTransactions();

    // Where a report is optional, the import starts with none rather than a suggested one.
    if (this.reportOptional) {
      this.selectedReport = null;
      return;
    }

    // Prefill report name from transaction date range
    const allTx = this.allTransactions;
    const dates = allTx
      .map(t => t.transactionDate)
      .filter(Boolean)
      .map(d => moment(d, ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD', 'D/MM/YYYY', 'D/M/YYYY'], true))
      .filter(m => m.isValid())
      .map(m => m.toDate());
    if (dates.length > 0) {
      const minDate = new Date(Math.min(...dates.map(d => d.getTime())));
      const maxDate = new Date(Math.max(...dates.map(d => d.getTime())));
      const sameMonth = minDate.getMonth() === maxDate.getMonth() &&
                        minDate.getFullYear() === maxDate.getFullYear();
      const suggestedName = sameMonth
        ? moment(minDate).format('MMMM YYYY') + ' Transactions'
        : moment(minDate).format('MMM') + ' – ' + moment(maxDate).format('MMM YYYY') + ' Transactions';
      // Pre-select an existing in-progress report with the same name; otherwise
      // seed a synthetic "new" tag so the user can hit Import without typing.
      const existing = this.selectableReports.find(
        r => r.reportName.toLowerCase() === suggestedName.toLowerCase()
      );
      this.selectedReport = existing ?? { id: '', reportName: suggestedName };
    } else {
      this.selectedReport = null;
    }
  }

  /** Normalise legacy string error into structured format until API is updated */
  private buildValidationErrors(item: any): ValidationError[] {
    if (!item.importStatus || item.importStatus === 'success') return [];
    return [{ field: 'Row', message: item.importStatus }];
  }

  // ── Review helpers ─────────────────────────────────────────

  clearSearch(): void {
    this.searchQuery = '';
    this.rebuildGroupedTransactions();
  }

  // ── Report selection ───────────────────────────────────────



  // ── Confirm import ─────────────────────────────────────────

  /** True when an existing report is selected; false when typing a new one. */
  get isExistingReport(): boolean {
    return !!this.selectedReport?.id;
  }

  /** Whether the review-step Import button can be enabled. */
  get canConfirmImport(): boolean {
    // A Refresh can apply status changes alone, and files new transactions in a report only if one is chosen.
    if (this.source === 'Refresh') return this.selectedCount + this.selectedChangeCount > 0;
    if (this.selectedCount === 0) return false;
    return this.reportOptional || !!this.selectedReport?.reportName?.trim();
  }

  /** Importing with no report chosen, which only the Transactions screen allows. */
  get isWithoutReport(): boolean {
    return this.reportOptional && !this.selectedReport?.reportName?.trim();
  }

  async confirmImport(): Promise<void> {
    const isRefresh = this.source === 'Refresh';
    if (!isRefresh && !this.isWithoutReport && !this.selectedReport?.reportName?.trim()) {
      this._toasterMessageService.displayToasterState(
        ToasterTypes.Warning, 'Required', 'Please select or create a report.'
      );
      return;
    }

    // Belt-and-braces: even if the UI somehow checked an "Already Exists" row,
    // never send it — the SP will drop it and the user gets a confusing count mismatch.
    const selectedTx = this.allTransactions.filter(t => t.isImport && this.isReimportable(t));
    // A Refresh with nothing new ticked, or no report chosen, needs no report resolved first.
    if (isRefresh && (selectedTx.length === 0 || !this.selectedReport?.reportName?.trim())) {
      await this.confirmRefresh(selectedTx, '');
      return;
    }
    if (selectedTx.length === 0) {
      this._toasterMessageService.displayToasterState(
        ToasterTypes.Warning, 'No Transactions', 'Please select at least one transaction to import.'
      );
      return;
    }

    // Resolve report ID — create when the synthetic tag (no id) is selected.
    let reportId: string;
    if (this.isWithoutReport) {
      reportId = '';
    } else if (this.isExistingReport) {
      reportId = this.selectedReport!.id;
    } else {
      const trimmedName = this.selectedReport!.reportName.trim();
      const newRpt = new NewReport(this._organizationService.selectedOrganization);
      newRpt.ReportName = trimmedName;
      newRpt.CreatedBy = sessionStorage.Id;
      newRpt.organizationId = this._organizationService.selectedOrganization;
      this.isSaving = true;
      const result = await this._reportService.createNewReport(newRpt).toPromise();
      this.isSaving = false;
      if (!result || result?.reportId === 'ConnectionLost' || result?.message) {
        this._toasterMessageService.displayToasterState(
          ToasterTypes.Error, null, result?.message || 'Failed to create report.'
        );
        return;
      }
      reportId = result.reportId;
      const created = { id: reportId, reportName: trimmedName };
      this.reports = [...this.reports, created];
      this.incompleteReports = [created, ...this.incompleteReports];
      this.selectedReport = created;
    }

    if (this.source === 'BankFeed') {
      await this.confirmBankFeedImport(selectedTx, reportId);
      return;
    }
    if (isRefresh) {
      await this.confirmRefresh(selectedTx, reportId);
      return;
    }

    this.isSaving = true;
    // Use the source-agnostic alias so the call site reads sensibly for both
    // spreadsheet and Xero imports (the legacy uploadImportExcel name is
    // misleading on the Xero path).
    const importCall = this.source === 'Report'
      ? this._filterService.importTransactionsToReport.bind(this._filterService)
      : this._filterService.uploadImportExcel.bind(this._filterService);
    const data: any = await importCall({
      organisationId: this._organizationService.selectedOrganization,
      userid: Number(localStorage.getItem('UserId')),
      data: JSON.stringify(selectedTx),
      Source: this.source,
      recordId: reportId,
      // Said outright: the server gives a blank recordId a fresh report id unless told otherwise.
      noReport: !reportId,
      isBankTransMode: true
    });

    this.isSaving = false;

    if (data.status === 1) {
      // Prefer the BE's actual insert count when it's available — historically the
      // SP silently dropped rows it couldn't insert (duplicates, out-of-range etc.)
      // and the FE happily reported "N imported" for whatever was sent. See
      // backend-import-allow-duplicates.md for the response contract.
      const inserted = typeof data.insertedCount === 'number' ? data.insertedCount : selectedTx.length;
      const requested = selectedTx.length;
      const dropped = Math.max(requested - inserted, 0);
      const msg = dropped > 0
        ? `${inserted} of ${requested} transactions imported · ${dropped} dropped server-side.`
        : `${inserted} transaction${inserted !== 1 ? 's' : ''} imported successfully.`;
      this._toasterMessageService.displayToasterState(
        dropped > 0 ? ToasterTypes.Warning : ToasterTypes.Success,
        dropped > 0 ? 'Imported with skips' : 'Success',
        msg
      );
      const bankPart = this.filter.accounts?.length > 0
        ? this.filter.accounts.map(s => `${s},`).join('').slice(0, -1)
        : '';
      const payload = this.source === 'Report'
        ? JSON.stringify({ account: bankPart, DateRangeSelcted: this.dateRangeSelected })
        : 'upload';
      this.importComplete.emit(payload);
    } else {
      this._toasterMessageService.displayToasterState(
        ToasterTypes.Error, 'Error', 'Import failed. Please try again.'
      );
    }
  }
}
